import { minBreakpoints, theme } from '@noths/polaris-client-ribbons-base';
import { mediaQuery } from '@noths/polaris-client-styles';
import { palette } from '@noths/polaris-design-system-theme-brand2026customer';
import { css } from 'styled-components';

const { fonts, responsiveText, spacing } = theme;

export const root = css`
  margin-bottom: ${spacing.xl}px;

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      display: flex;
      flex-direction: column;
      padding-top: ${spacing.lg}px;
      background-color: ${palette.blue5};
    `,
  )}
`;

export const coverImageBackground = (backgroundImageSrc: string) => {
  return css`
    position: relative;
    width: 100%;
    height: 233px;
    background-size: cover;
    background-position: center;
    background-image: url(${backgroundImageSrc});

    ${mediaQuery(
      { min: minBreakpoints.M },
      css`
        display: none;
      `,
    )}
  `;
};

export const logo = css`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 112px;
  height: 112px;
  border-radius: ${spacing.md}px;
  border: ${spacing.xs}px solid var(--neutral-white-base);
  box-shadow: 0 4px 8px 0 rgba(30, 30, 41, 0.16);
  background-color: var(--neutral-white-base);
  overflow: hidden;

  img {
    display: block;
    border-radius: ${spacing.xs}px;
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    object-position: center;
    margin: auto;
  }
`;

export const logoOnCoverImage = css`
  position: absolute;
  left: ${spacing.md}px;
  bottom: ${spacing.sm}px;

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      display: none;
    `,
  )}
`;

export const logoWithIntroText = css`
  display: none;
  margin-bottom: ${spacing.md}px;

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      display: flex;
    `,
  )}
`;

export const infoStrip = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: ${spacing.md}px;
  margin: 0;
  padding: ${spacing.xs}px;
  height: 58px;
  background-color: var(--brand-purple-5);
  list-style: none;
  color: var(--neutral-black-base);

  li {
    flex-grow: 1;
    text-align: center;
    position: relative;
    font-family: ${fonts.PRIMARY_REGULAR.family};
    font-weight: ${fonts.PRIMARY_REGULAR.weight};
    ${responsiveText.BODY.XS};

    &:not(:first-child)::before {
      content: '';
      position: absolute;
      left: calc(-${spacing.md}px / 2);
      top: 50%;
      transform: translateY(-50%);
      width: 1px;
      height: 24px;
      background-color: var(--brand-bluple-30);
    }
  }

  b {
    ${responsiveText.BODY.SM};
  }

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      order: 2;
      margin: 0;

      li {
        ${responsiveText.BODY.SM};
      }
    `,
  )}
`;

export const infoItemValue = (options?: { borderColor?: string; textColor?: string }) => {
  const { borderColor = 'transparent', textColor = 'inherit' } = options || {};

  return css`
    display: block;
    width: fit-content;
    margin: 0 auto 1px auto;
    border: 1px solid ${borderColor};
    color: ${textColor};
    padding: 1px ${spacing.xs}px;
    border-radius: ${spacing.md}px;

    ${mediaQuery(
      { min: minBreakpoints.M },
      css`
        display: inline-block;
        width: auto;
        margin: 0;
      `,
    )}
  `;
};

export const getRatingColors = (rating: number) => {
  if (rating < 3) {
    return {
      borderColor: 'var(--functional-red-50)',
      textColor: 'var(--functional-red-base)',
    };
  } else if (rating < 4) {
    return {
      borderColor: 'var(--functional-yellow-base)',
      textColor: '#998200', // no palette color with accessible contrast, so using a custom color here
    };
  }
  return {
    borderColor: 'var(--functional-green-base)',
    textColor: 'var(--functional-green-deep)',
  };
};

export const columns = css`
  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      display: flex;
      flex-direction: row;
      gap: ${spacing.xl}px;

      order: 1;
      margin: 0 ${spacing.xl}px;

      & > div {
        width: 50%;
      }
    `,
  )}
`;

export const introTextColumn = css`
  margin-top: ${spacing.md}px;
  padding: 0 ${spacing.lg}px;

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      padding: 0;
    `,
  )};
`;

export const heading = css`
  margin: 0 0 ${spacing.base}px 0;
  font-family: ${fonts.PRIMARY_SEMIBOLD.family};
  font-weight: ${fonts.PRIMARY_SEMIBOLD.weight};
  ${responsiveText.HEADING.MD}
  color: var(--neutral-black-base);
`;

export const location = css`
  display: inline-flex;
  column-gap: ${spacing.base}px;
  margin: 0 0 ${spacing.md}px 0;
`;

export const introText = css`
  margin: 0 0 ${spacing.md}px 0;
  font-family: ${fonts.PRIMARY_REGULAR.family};
  font-weight: ${fonts.PRIMARY_REGULAR.weight};
  ${responsiveText.BODY.MD}
`;

export const topBrand = css`
  margin-bottom: ${spacing.md}px;
  border: 1px solid var(--brand-purple-10);
  border-radius: ${spacing.xs}px;
  padding: ${spacing.sm}px ${spacing.md}px;
  font-family: ${fonts.PRIMARY_REGULAR.family};
  font-weight: ${fonts.PRIMARY_REGULAR.weight};
  text-align: center;

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      display: flex;
      align-items: center;
      column-gap: ${spacing.md}px;
      border-color: var(--brand-purple-30);

      p {
        text-align: left;
      }
    `,
  )};

  strong {
    display: block;
    margin-bottom: ${spacing.base}px;
    ${responsiveText.BODY.MD}
    white-space: nowrap;

    > span {
      display: block;
      margin: 0 auto;
    }
  }

  p {
    margin: 0;
    ${responsiveText.BODY.SM}
  }
`;

export const coverImage = css`
  display: none;

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      display: block;

      img {
        border-radius: ${spacing.xs}px;
        width: 100%;
      }
    `,
  )}
`;
